import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '../services/supabaseClient';
import { getStoredSession } from '../hooks/useAuth';

interface Assignment {
  id: string;
  title: string;
  subject: string | null;
  deadline: string;
  instructions: string | null;
  question_count: number;
  submitted_at: string | null;
  score: number | null;
  total: number | null;
}

export default function Assignments() {
  const session = getStoredSession();
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!session?.student_id) return;

    supabase.rpc('get_student_assessment_list', {
      p_student_id: session.student_id,
    }).then(({ data, error }) => {
      if (!error && data?.length) {
        const list: Assignment[] = data.map((row: any) => ({
          id: row.id,
          title: row.title,
          subject: row.subject,
          deadline: row.deadline,
          instructions: row.instructions,
          question_count: row.question_count,
          submitted_at: row.submitted_at ?? null,
          score: row.score ?? null,
          total: row.total ?? null,
        }));
        setAssignments(list);

        // Mark unseen notifications as seen
        const unseenIds = data.filter((r: any) => !r.seen_at).map((r: any) => r.id);
        if (unseenIds.length) {
          supabase.rpc('mark_assessments_seen', {
            p_student_id: session.student_id,
            p_assessment_ids: unseenIds,
          });
        }
      }
      setLoading(false);
    });
  }, [session?.student_id]);

  const isPast = (d: string) => new Date(d) < new Date();

  if (loading) {
    return (
      <main className="flex-1 max-w-2xl mx-auto w-full px-4 py-8">
        <div className="flex justify-center py-20">
          <div className="w-6 h-6 border-2 border-coral-warm border-t-transparent rounded-full animate-spin" />
        </div>
      </main>
    );
  }

  return (
    <main className="flex-1 max-w-2xl mx-auto w-full px-4 py-6 pb-24">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-ocean-deep">Assignments</h1>
        <p className="text-sm text-gray-500 mt-1">Tests assigned by your teacher</p>
      </div>

      {assignments.length === 0 ? (
        <div className="text-center py-20">
          <ClipboardList className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No assignments yet</p>
          <p className="text-sm text-gray-400 mt-1">Your teacher will assign tests here</p>
        </div>
      ) : (
        <div className="space-y-3">
          {assignments.map(a => {
            const expired = isPast(a.deadline);
            const submitted = !!a.submitted_at;
            const pct = submitted && a.total ? Math.round(((a.score ?? 0) / a.total) * 100) : null;

            return (
              <div
                key={a.id}
                className="bg-white rounded-xl border border-gray-200 p-4 hover:border-coral-warm hover:shadow-sm transition-all"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 mb-1">{a.title}</h3>
                    {a.subject && <p className="text-xs text-gray-500 mb-2">{a.subject}</p>}
                    <div className="flex flex-wrap gap-3 text-xs text-gray-400 mb-3">
                      <span className="flex items-center gap-1">
                        <ClipboardList className="w-3 h-3" />{a.question_count} questions
                      </span>
                      <span className={`flex items-center gap-1 ${expired && !submitted ? 'text-red-400' : ''}`}>
                        <Clock className="w-3 h-3" />
                        {expired ? 'Closed' : `Due ${new Date(a.deadline).toLocaleDateString()}`}
                      </span>
                    </div>
                    {a.instructions && (
                      <p className="text-xs text-gray-500 italic mb-3">"{a.instructions}"</p>
                    )}
                  </div>

                  {submitted && pct !== null && (
                    <div className="flex-shrink-0 text-right">
                      <div className={`text-lg font-bold ${
                        pct >= 70 ? 'text-green-600' : pct >= 40 ? 'text-amber-600' : 'text-red-500'
                      }`}>{pct}%</div>
                      <div className="text-xs text-gray-400">{a.score}/{a.total}</div>
                    </div>
                  )}
                </div>

                {submitted ? (
                  <Link
                    to={`/assignments/${a.id}/take`}
                    className="flex items-center justify-between gap-2 text-xs text-green-600 font-medium hover:text-green-700"
                  >
                    <span className="flex items-center gap-1.5">
                      <CheckCircle className="w-4 h-4" />
                      Submitted {new Date(a.submitted_at!).toLocaleDateString()}
                    </span>
                    <span className="text-coral-warm font-semibold">Review answers →</span>
                  </Link>
                ) : expired ? (
                  <div className="flex items-center gap-2 text-xs text-red-400 font-medium">
                    <AlertCircle className="w-4 h-4" />
                    Deadline passed
                  </div>
                ) : (
                  <Link
                    to={`/assignments/${a.id}/take`}
                    className="block w-full text-center py-2.5 bg-coral-warm hover:bg-orange-600 text-white rounded-lg text-sm font-semibold transition-colors"
                  >
                    Start Test →
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      )}
    </main>
  );
}
